import { useContext, useEffect, useState } from 'react'
import { useInterval } from 'usehooks-ts'
import axios from 'axios'
import { observer } from 'mobx-react'
import { Header, LoadingSpinner, Button } from '../../Generic'
import { StoreContext } from '@store/Root.store'
import { usePageVisibility } from '@hooks/usePageVisibility'

import '../SwitchToMobile.css'
import { devLog } from '@methods/devLog'
import { forceRedirect } from '@methods/forceRedirect'
import { actionReport } from '@methods/actionReport'
import { Illustration } from '@/components/Illustration/Illustration'

interface ShowPinProps {
  nextStep: () => void
  stayUrl?: string
  generatePinCodeUrl?: string
  isPinCodeConsumedUrl?: string
  closeDesktopSessionUrl?: string
  shouldProceedToPollingStep?: boolean
}

export const ShowPin = observer(
  ({
    nextStep,
    stayUrl,
    generatePinCodeUrl,
    isPinCodeConsumedUrl,
    closeDesktopSessionUrl,
    shouldProceedToPollingStep,
  }: ShowPinProps) => {
    const store = useContext(StoreContext)
    const { isMobile } = store.AppState
    const { switchToMobile: trans } = store.TranslationsState.translationsNew

    const [loading, setLoading] = useState(true)
    const [isPinCodeConsumed, setIsPinCodeConsumed] = useState(false)
    const [error, setError] = useState(false)
    const [pinCode, setPinCode] = useState('0000')
    const [shouldPoll, setShouldPoll] = useState(true)

    const isVisible = usePageVisibility()

    const getCode = async () => {
      if (!generatePinCodeUrl) return
      try {
        const { data } = await axios.post(
          generatePinCodeUrl,
          {},
          {
            withCredentials: true,
          }
        )

        setPinCode(data.pinCode)
        setLoading(false)
      } catch (error) {
        devLog(error)
      }
    }

    const checkIfPinCodeIsConsumed = async () => {
      if (!isPinCodeConsumedUrl || !closeDesktopSessionUrl) return

      try {
        const { data } = await axios.get(isPinCodeConsumedUrl, {
          withCredentials: true,
        })

        if (!data.isConsumed) return
        setIsPinCodeConsumed(true)

        if (shouldProceedToPollingStep) {
          nextStep()
        } else if (data?.shouldCloseSession) {
          forceRedirect(closeDesktopSessionUrl)
        } else {
          setError(true)

          actionReport({
            type: 'event.onboarding-web.go-to-mobile.DESKTOP_WRONG_PIN_PAGE_OPENED',
            payload: {},
          })
        }
      } catch (error) {
        devLog(error)
      }
    }

    useEffect(() => {
      actionReport({
        type: 'event.onboarding-web.go-to-mobile.DESKTOP_SHOW_PIN_PAGE_OPENED',
        payload: {},
      })

      void getCode()
    }, [])

    useEffect(() => {
      setShouldPoll(isVisible)
    }, [isVisible])

    useInterval(
      checkIfPinCodeIsConsumed,
      !loading && !isPinCodeConsumed && shouldPoll ? 3000 : null
    )

    return !error ? (
      <>
        <Header>{trans.showPin.header}</Header>

        <p className="generic__paragraph">{trans.showPin.description}</p>

        <div className="switch-to-mobile__code-wrapper">
          {!loading && (
            <div
              className="generic__paragraph-with-border switch-to-mobile__code"
              data-testid="pinCode"
            >
              {pinCode}
            </div>
          )}

          <LoadingSpinner className="switch-to-mobile__show-pin-spinner" />

          <p className="generic__intro-paragraph">{trans.showPin.spinner}</p>
        </div>

        <div className="generic__divider switch-to-mobile__divider--full" />
      </>
    ) : (
      <>
        <Illustration type="device-not-switched" />
        <Header className="m-0-0-30">{trans.showPin.errorHeader}</Header>
        <p className="generic__paragraph-centered m-0-0-30">{trans.showPin.errorDescription}</p>

        <div className="layout__content-container-bottom-fixed">
          {!isMobile ? <div className="layout__content-container-bottom-left" /> : <></>}
          <Button
            onClick={() => {
              if (stayUrl?.length) {
                forceRedirect(stayUrl)
              }
            }}
          >
            <span className="generic__button-text">{trans.showPin.errorProceed}</span>
          </Button>
        </div>
        <div className="layout__mobile-spacer-for-content-container-bottom-fixed" />
      </>
    )
  }
)
